import React, { useRef, useState } from 'react';
import { Grid, Button, Box } from '@mui/material';
import AttachmentSharpIcon from '@mui/icons-material/AttachmentSharp';
import ClearIcon from '@mui/icons-material/Clear';

import Attachment from '../Attachment';
import { ATTACHMENT_VARS } from '../../constants';

type Props = {
  attachment: File | null;
  setAttachment: (file: File | null) => void;
};

const AttachmentButton = ({ attachment, setAttachment }: Props) => {
  const [inputKey, setInputKey] = useState<number>(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (file.size > ATTACHMENT_VARS.MAX_FILE_SIZE) {
        setError(`${file.name} could not be attached because it is over the 2MB limit.`);
        setAttachment(null);
        return;
      }

      if (!ATTACHMENT_VARS.ACCEPTED_FILE_TYPES.includes(file.type)) {
        setError(
          `${file.name} could not be attached because it is an invalid file type: ${file.type}`,
        );
        setAttachment(null);
        return;
      }
    }
    setError('');
    setAttachment(file);
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileRemove = () => {
    setAttachment(null);
    setInputKey((prevKey) => prevKey + 1);
  };

  return (
    <>
      <Grid item xs={12}>
        <Box
          component="input"
          key={inputKey}
          id="attachment"
          data-testid="file-input"
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={ATTACHMENT_VARS.ACCEPTED_FILE_TYPES_ATTR}
          name="attachment"
          placeholder="Attach file"
          sx={{
            visibility: 'hidden',
            position: 'relative',
            zIndex: -1,
            width: '100% ',
          }}
        />
      </Grid>
      {attachment ? (
        <Grid container>
          <Grid item>
            <Attachment
              attachment={attachment}
              handleClick={handleFileRemove}
              endIcon={<ClearIcon />}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Button
              onClick={handleButtonClick}
              startIcon={<AttachmentSharpIcon />}
              sx={{
                color: 'link.main',
                textTransform: 'none',
                textDecoration: 'underline',
                fontWeight: 600,
              }}
            >
              Attach file
            </Button>
          </Grid>
          <Grid item xs={12} sx={{ fontStyle: 'italic', fontSize: 'smaller', mb: 1, ml: 1 }}>
            (gif, jpg, pdf, heic, or png under 2MB)
          </Grid>
          {error && (
            <Grid item xs={12} sx={{ fontSize: 'smaller', ml: 1, color: 'error.main' }}>
              {error}
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default AttachmentButton;
